import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    HostBinding,
    Input,
    OnInit,
    Renderer2,
    TemplateRef
} from '@angular/core';
import {merge, Observable, timer} from 'rxjs';
import {share, takeUntil, tap} from 'rxjs/operators';
import {destroy$} from '../../../components/destroyable';
import {ModalService} from '../../../components/modal';
import {ALERT_COMMON_STRINGS} from '../../../components/modal/alert-modal';
import {OverlayService} from '../../../components/overlay';
import {CellSizeObserverService} from '../../../pages/dashboard/layout/cell/cell-size-observer.service';

const WIDE_CLASS = '-wide';
const FULL_SIZE_CLASS = '-full-size';
const HIDDEN_CLASS = '-hidden';

@Component({
    selector: 'widget-inner-alert',
    templateUrl: './widget-inner-alert.component.html',
    styleUrls: ['./widget-inner-alert.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class WidgetInnerAlertComponent implements OnInit {

    @Input()
    public title: string;

    @Input()
    public titleTranslate: boolean = true;

    @Input()
    public messageTemplate: TemplateRef<any>;

    @Input()
    public message: string;

    @Input()
    public messageTranslate: boolean = true;

    @Input()
    public messageTranslationParams: { [key: string]: string } = null;

    @Input()
    public buttonText: string = ALERT_COMMON_STRINGS.buttonOk;

    @Input()
    public buttonRouterLink: any[];

    @Input()
    @HostBinding(`class.${WIDE_CLASS}`)
    public isWide: boolean = false;

    @HostBinding(`class.${FULL_SIZE_CLASS}`)
    public isFullSize: boolean = false;

    @HostBinding(`class.${HIDDEN_CLASS}`)
    public isHidden: boolean = true; // Initially hide the modal before the full-size mode is not checked.

    public readonly destroy$ = destroy$();

    public onConfirmCallback: () => void;

    protected readonly resize$ = this.createResizeStream();

    constructor(
        protected elementRef: ElementRef,
        protected modalService: ModalService,
        protected overlayService: OverlayService,
        protected cellSizeObserverService: CellSizeObserverService,
        protected renderer: Renderer2
    ) {}

    public onCloseCallback: () => void = () => null;

    public ngOnInit(): void {
        this.createOverlay();
        this.listenToWidgetSize();
    }

    public confirm(): void {
        this.onConfirmCallback();
        this.close();
    }

    public close(): void {
        this.modalService.close(this);
        this.onCloseCallback();
    }

    private createOverlay(): void {
        this.overlayService.create({
            target: this.modalService.getByInstance(this)
        }).subscribe(() => this.close());
    }

    private createResizeStream(): Observable<unknown> {
        const initialCheck$ = timer(100); // A short delay in order to allow DOM to be initialized.

        return merge(
            initialCheck$,
            this.cellSizeObserverService.resize$
        ).pipe(
            tap(() => this.updateFullSizeState()),
            takeUntil(this.destroy$),
            share()
        );
    }

    private listenToWidgetSize(): void {
        this.resize$.subscribe();
    }

    private updateFullSizeState(): void {
        const modalElement: HTMLElement = this.elementRef.nativeElement;
        const parentElement: HTMLElement = this.renderer.parentNode(this.elementRef.nativeElement);

        this.renderer.removeClass(modalElement, FULL_SIZE_CLASS);

        const modalRect = modalElement.getBoundingClientRect();
        const parentRect = parentElement.getBoundingClientRect();

        const isModalOverflowed = modalRect.width > parentRect.width || modalRect.height > parentRect.height;

        this.isFullSize = isModalOverflowed;

        if (isModalOverflowed) {
            this.renderer.addClass(modalElement, FULL_SIZE_CLASS);
        }

        this.isHidden = false;
        this.renderer.removeClass(modalElement, HIDDEN_CLASS); // Apply w/o waiting for Change Detection.
    }

}
